import { motion } from "framer-motion";
import { ShieldCheck, X, Printer, BookOpen, Download } from "lucide-react";

const BOOKS = [
  {
    title: "The Psychology of Money",
    author: "Morgan Housel",
    points: [
      "Your behavior with money matters more than intelligence.",
      "Don't risk everything for quick profit.",
      "Keep emergency cash.",
      "Don't confuse income with wealth.",
      "Protect your capital before trying to grow it."
    ],
    remember: "Protect your money first. Grow it second."
  },
  {
    title: "Influence",
    author: "Robert Cialdini",
    points: [
      "People can influence you through friendship, favors, authority, social pressure, scarcity, commitment, and emotional pressure.",
      "If someone says, “Trust me, we’re friends,” don’t make the decision based only on the relationship.",
      "Ask: “What evidence do I have?”"
    ],
    remember: "Trust people, but verify important things."
  },
  {
    title: "Thinking, Fast and Slow",
    author: "Daniel Kahneman",
    points: [
      "Your emotions can make you make fast decisions that later become expensive mistakes.",
      "Before a large business decision, ask: What do I gain? What can I lose? What evidence do I have? What is the worst-case scenario? What happens if I wait 24 hours?"
    ],
    remember: "Big decisions need calm thinking."
  },
  {
    title: "Never Split the Difference",
    author: "Chris Voss",
    points: [
      "Good negotiation isn’t just saying, “Give me a lower price.”",
      "Understand the other person's position while protecting your own.",
      "Useful question: “How can we make this work for both of us?”",
      "Never negotiate when you're desperate to close the deal."
    ],
    remember: "The person who can walk away has more negotiating power."
  },
  {
    title: "The Richest Man in Babylon",
    author: "George S. Clason",
    points: [
      "Learn to control money before trying to make more money.",
      "Always know: Revenue − Expenses = Profit.",
      "Cash available is not the same as profit.",
      "A business can show profit on paper but still have cash-flow problems."
    ],
    remember: "Make money, keep money, and make money work for you."
  },
  {
    title: "The Intelligent Investor",
    author: "Benjamin Graham",
    points: [
      "Don’t focus only on: “How much can I make?”",
      "Also ask: “How much can I lose?”",
      "A good business decision considers both opportunity and downside risk."
    ],
    remember: "Avoiding a major loss is a form of winning."
  },
  {
    title: "The Art of Spending Money",
    author: "Morgan Housel",
    points: [
      "Making money is one skill. Knowing how to spend it wisely is another.",
      "The purpose of money is not simply to become rich; it is to build a life you enjoy."
    ],
    remember: "Earn wisely. Save intelligently. Spend intentionally.",
    stickyNotes: [
      ["Money should buy freedom.", "The greatest luxury is having control over your time."],
      ["Don't spend to impress people.", "Other people's opinions cannot build your happiness."],
      ["Wealth is what you don't see.", "Money saved and invested is often more powerful than expensive things you show."],
      ["Understand your personal happiness.", "What makes another person happy may not make you happy."],
      ["Spend on experiences.", "Good memories can give you happiness long after the money is gone."],
      ["Avoid lifestyle inflation.", "When your income increases, don't automatically increase your spending."],
      ["Buy time, not just things.", "Spend money on things that reduce unnecessary stress and save valuable time."],
      ["Don't confuse expensive with valuable.", "A high price doesn't always mean a better product or experience."],
      ["Know when enough is enough.", "Without knowing what is enough, you can spend your entire life chasing more."],
      ["Protect your future.", "Every unnecessary expense takes money away from future opportunities."],
      ["Spend on relationships.", "Helping family and creating meaningful memories can be more valuable than luxury."],
      ["Don't let money control you.", "Money is a tool. You should control it, not become its servant."],
      ["Avoid comparison.", "Someone else's lifestyle is not your financial target."],
      ["Spend according to your values.", "Put money toward what genuinely matters to you."],
      ["Build a life you don't need to escape from.", "The ultimate goal is not to look rich but to live well."]
    ],
    businessRules: [
      ["Separate business money from personal money.", "Never mix company capital with personal expenses."],
      ["Protect your capital.", "Don't spend money needed for your next shipment or business operation."],
      ["Buy assets before luxuries.", "Build income-producing opportunities before expensive lifestyles."],
      ["Keep emergency money.", "Unexpected problems should not destroy your business."],
      ["Enjoy some of your success.", "Don't work your entire life without enjoying the results."]
    ],
    example: {
      title: "Imagine you receive 1,000,000 birr in profit",
      rows: [
        ["Reinvest in business", "400,000"],
        ["Save for future", "200,000"],
        ["Family and personal needs", "150,000"],
        ["Emergency reserve", "150,000"],
        ["Enjoyment", "100,000"]
      ],
      total: ["Total", "1,000,000"],
      note: "Illustrative example, not a fixed financial formula. First account for taxes, debts and business obligations."
    },
    questions: [
      "Do I really need this?",
      "Will this purchase make my life better?",
      "Am I buying it because I want to impress someone?",
      "Will this money be more valuable in my business?",
      "Will I regret this purchase after 30 days?",
      "Does this decision protect my family's future?"
    ],
    bigLesson: [
      "The purpose of becoming wealthy is not to spend everything you earn. It is to have the freedom to choose how you live, whom you help, and how you use your time.",
      "Money + Wisdom = Freedom",
      "Money without wisdom can disappear. Money with wisdom can build generations.",
      "Are you trying to become rich so people can see your wealth, or so you and your family can experience freedom?"
    ]
  }
];

const BUSINESS_RULES = [
  "Separate friendship from business.",
  "Put important agreements in writing.",
  "Verify before sending money.",
  "Never depend on only one person's information.",
  "Don't let urgency make your decision.",
  "Don't make major decisions while angry or excited.",
  "Always calculate the worst-case scenario.",
  "If a deal looks too good, investigate more.",
  "Don't confuse confidence with competence.",
  "Protect your capital before chasing profit."
];

const ATTACHED_READINGS = [
  {
    title: "The 48 Laws of Power — Robert Greene",
    description: "Amharic summary of all 48 laws — understand power, achieve success, and protect yourself from manipulation.",
    href: "/reading/48-laws-of-power-amharic.pdf",
    downloadName: "48-Laws-of-Power-Amharic.pdf",
    lang: "Amharic",
    key: "48-laws"
  },
  {
    title: "100 Laws of Power and Money (100 የሥልጣንና የገንዘብ ሕጎች)",
    description: "Amharic guide of 100 principles — laws 1–50 on power, people, leadership and negotiation; laws 51–100 on earning, saving, investing and financial freedom.",
    href: "/reading/100-laws-of-power-and-money-amharic.pdf",
    downloadName: "100-Laws-of-Power-and-Money-Amharic.pdf",
    lang: "Amharic",
    key: "100-laws"
  },
  {
    title: "Import & Export Business — Management Sticky Notes",
    description: "Practical management notes for running an import/export business from Ethiopia — money control, goods control, people control, the import/export process, financial control, and employee management.",
    href: "/reading/import-export-management-sticky-notes.pdf",
    downloadName: "Import-Export-Management-Sticky-Notes.pdf",
    lang: "English",
    key: "sticky-notes"
  },
  {
    title: "Niccolò Machiavelli — Sticky Notes on Power, Money, Human Nature & Leadership",
    description: "Amharic sticky notes from Machiavelli's teachings — human nature, power and leadership, money and business, strategy and decision-making, and relationships and partnerships.",
    href: "/reading/machiavelli-sticky-notes-amharic.pdf",
    downloadName: "Machiavelli-Sticky-Notes-Amharic.pdf",
    lang: "Amharic",
    key: "machiavelli"
  },
  {
    title: "Think and Grow Rich — Napoleon Hill",
    description: "Amharic summary of the classic on wealth and mindset — desire, faith, autosuggestion, specialized knowledge, organized planning, persistence, the master mind, and overcoming the six fears.",
    href: "/reading/think-and-grow-rich-amharic.pdf",
    downloadName: "Think-and-Grow-Rich-Amharic.pdf",
    lang: "Amharic",
    key: "think-grow-rich"
  }
];

const DECISION_FORMULA = [
  ["STOP", "Don't answer immediately."],
  ["CHECK", "Check documents, prices, people, and numbers."],
  ["THINK", "Consider the risks."],
  ["VERIFY", "Get independent information."],
  ["DECIDE", "Then make your decision."]
];

const BusinessProtectionModal = ({ onClose }) => {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm no-print"
      onClick={onClose}
      data-testid="business-protection-modal"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="trade-card business-protection-shell rounded-sm p-6 w-full max-w-4xl max-h-[88vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 mb-6 no-print">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 bg-[#D4AF37]/10 rounded-sm flex items-center justify-center flex-shrink-0">
              <ShieldCheck className="w-5 h-5 trade-gold" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.25em] trade-gold">10-Minute Reading</p>
              <h2 className="text-2xl font-serif font-bold" data-testid="business-protection-title">Business Protection Reading</h2>
              <p className="text-xs text-white/45 mt-1">Better decisions, stronger relationships, protected money</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => window.print()}
              className="trade-btn-primary rounded-sm h-10 px-4 text-sm flex items-center gap-2"
              data-testid="business-protection-print-btn"
            >
              <Printer size={15} /> Print
            </button>
            <button onClick={onClose} className="p-2 text-white/40 hover:text-white" data-testid="business-protection-close">
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="business-protection-print bg-white text-slate-900 rounded-sm p-6 md:p-8" data-testid="business-protection-content">
          <div className="text-center mb-8">
            <p className="text-[10px] uppercase tracking-[0.3em] text-amber-700 mb-2">10-Minute Business Protection Reading</p>
            <h1 className="text-3xl font-serif font-bold mb-2">Protect Your Money and Your Decisions</h1>
            <p className="text-sm text-slate-600">Business lessons for better decisions, stronger relationships, and protecting your money.</p>
          </div>

          <div className="space-y-6 mb-10" data-testid="business-protection-books">
            {BOOKS.map((book, index) => (
              <div key={book.title} className="p-4 border border-slate-300 rounded-sm" data-testid={`business-book-${index + 1}`}>
                <p className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Lesson {index + 1}</p>
                <h2 className="font-serif font-bold text-lg">
                  {book.title} <span className="font-normal text-slate-500 text-sm">— {book.author}</span>
                </h2>
                <ul className="list-disc pl-5 mt-2 space-y-1 text-sm text-slate-700">
                  {book.points.map(point => (
                    <li key={point}>{point}</li>
                  ))}
                </ul>
                {book.stickyNotes && (
                  <div className="mt-4" data-testid={`spending-sticky-notes`}>
                    <p className="text-[10px] uppercase tracking-widest text-amber-700 mb-2">15 Powerful Sticky Notes</p>
                    <ol className="space-y-2">
                      {book.stickyNotes.map(([lead, text], i) => (
                        <li key={lead} className="flex items-start gap-3 p-3 bg-amber-50/70 border border-amber-200 rounded-sm" data-testid={`spending-sticky-${i + 1}`}>
                          <span className="w-6 h-6 rounded-sm bg-amber-100 text-amber-800 flex items-center justify-center flex-shrink-0 text-xs font-bold">{i + 1}</span>
                          <span className="text-sm"><span className="font-semibold">{lead}</span> <span className="text-slate-600">{text}</span></span>
                        </li>
                      ))}
                    </ol>
                  </div>
                )}
                {book.businessRules && (
                  <div className="mt-4" data-testid="spending-business-rules">
                    <p className="text-[10px] uppercase tracking-widest text-amber-700 mb-2">Five Rules for Business People</p>
                    <ol className="space-y-1.5">
                      {book.businessRules.map(([lead, text], i) => (
                        <li key={lead} className="text-sm flex items-start gap-2" data-testid={`spending-rule-${i + 1}`}>
                          <span className="font-bold text-amber-800 flex-shrink-0">{i + 1}.</span>
                          <span><span className="font-semibold">{lead}</span> <span className="text-slate-600">{text}</span></span>
                        </li>
                      ))}
                    </ol>
                  </div>
                )}
                {book.example && (
                  <div className="mt-4" data-testid="spending-example">
                    <p className="text-[10px] uppercase tracking-widest text-amber-700 mb-2">Practical Example</p>
                    <p className="text-sm text-slate-700 mb-2">{book.example.title}</p>
                    <div className="border border-slate-300 rounded-sm overflow-hidden">
                      {book.example.rows.map(([purpose, amount], i) => (
                        <div key={purpose} className={`grid grid-cols-2 text-sm ${i % 2 === 0 ? "bg-slate-50" : "bg-white"}`}>
                          <p className="p-2.5 border-r border-slate-300 text-slate-700">{purpose}</p>
                          <p className="p-2.5 text-right font-semibold">{amount}</p>
                        </div>
                      ))}
                      <div className="grid grid-cols-2 text-sm bg-amber-100 border-t border-amber-300">
                        <p className="p-2.5 border-r border-amber-300 font-bold">{book.example.total[0]}</p>
                        <p className="p-2.5 text-right font-bold">{book.example.total[1]}</p>
                      </div>
                    </div>
                    <p className="text-xs text-slate-500 mt-2">{book.example.note}</p>
                  </div>
                )}
                {book.questions && (
                  <div className="mt-4" data-testid="spending-questions">
                    <p className="text-[10px] uppercase tracking-widest text-amber-700 mb-2">Before Spending a Large Amount, Ask Yourself</p>
                    <ul className="space-y-1.5">
                      {book.questions.map((q, i) => (
                        <li key={q} className="text-sm text-slate-700 flex items-start gap-2" data-testid={`spending-question-${i + 1}`}>
                          <span className="text-amber-700 font-bold flex-shrink-0">?</span>
                          {q}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <p className="mt-3 text-sm font-semibold text-amber-800 bg-amber-50 border-l-2 border-amber-600 pl-3 py-1">
                  Remember: {book.remember}
                </p>
                {book.bigLesson && (
                  <div className="mt-4 bg-slate-900 text-white rounded-sm p-4 text-center" data-testid="spending-big-lesson">
                    <p className="text-[10px] uppercase tracking-[0.3em] text-amber-400 mb-2">The Biggest Lesson</p>
                    {book.bigLesson.map(line => (
                      <p key={line} className={`text-sm mt-2 ${line.includes("=") ? "font-serif font-bold text-lg text-amber-300" : "text-white/80"}`}>{line}</p>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mb-10 space-y-4" data-testid="business-protection-readings">
            <p className="text-[10px] uppercase tracking-[0.3em] text-amber-700">Attached Reading · PDFs</p>
            {ATTACHED_READINGS.map(reading => (
              <div key={reading.key} className="p-4 border-2 border-amber-600 rounded-sm bg-amber-50" data-testid={`business-protection-pdf-${reading.key}`}>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-amber-600/15 rounded-sm flex items-center justify-center flex-shrink-0">
                    <BookOpen size={18} className="text-amber-700" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h2 className="font-serif font-bold text-lg">{reading.title}</h2>
                      <span className="text-[10px] uppercase tracking-widest bg-amber-600/15 text-amber-800 px-2 py-0.5 rounded-sm">{reading.lang}</span>
                    </div>
                    <p className="text-sm text-slate-600 mt-1">{reading.description}</p>
                    <div className="flex flex-wrap gap-2 mt-3 no-print">
                      <a
                        href={reading.href}
                        target="_blank"
                        rel="noreferrer"
                        className="bg-slate-900 text-white text-sm px-4 h-10 rounded-sm flex items-center gap-2 hover:bg-slate-700"
                        data-testid={`business-protection-pdf-open-${reading.key}`}
                      >
                        <BookOpen size={15} /> Read PDF
                      </a>
                      <a
                        href={reading.href}
                        download={reading.downloadName}
                        className="border border-slate-900 text-slate-900 text-sm px-4 h-10 rounded-sm flex items-center gap-2 hover:bg-slate-100"
                        data-testid={`business-protection-pdf-download-${reading.key}`}
                      >
                        <Download size={15} /> Download
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mb-10" data-testid="business-protection-rules">
            <h2 className="font-serif font-bold text-xl mb-3">Your 10 Business Rules</h2>
            <ol className="space-y-2">
              {BUSINESS_RULES.map((rule, index) => (
                <li key={rule} className="flex items-start gap-3 p-3 border border-slate-300 rounded-sm" data-testid={`business-rule-${index + 1}`}>
                  <span className="w-6 h-6 rounded-sm bg-amber-100 text-amber-800 flex items-center justify-center flex-shrink-0 text-xs font-bold">
                    {index + 1}
                  </span>
                  <span className="text-sm font-semibold">{rule}</span>
                </li>
              ))}
            </ol>
          </div>

          <div className="mb-10" data-testid="business-protection-formula">
            <h2 className="font-serif font-bold text-xl mb-3">Your Business Decision Formula</h2>
            <div className="flex flex-wrap items-center gap-2 mb-4">
              {DECISION_FORMULA.map(([step], index) => (
                <span key={step} className="flex items-center gap-2">
                  <span className="bg-slate-900 text-white text-xs font-bold tracking-widest px-3 py-1.5 rounded-sm">{step}</span>
                  {index < DECISION_FORMULA.length - 1 && <span className="text-slate-400 font-bold">→</span>}
                </span>
              ))}
            </div>
            <div className="space-y-2">
              {DECISION_FORMULA.map(([step, description]) => (
                <p key={step} className="text-sm text-slate-700" data-testid={`formula-step-${step.toLowerCase()}`}>
                  <span className="font-bold text-slate-900">{step}:</span> {description}
                </p>
              ))}
            </div>
          </div>

          <div className="border-t-2 border-slate-900 pt-6 text-center" data-testid="business-protection-reminder">
            <p className="text-[10px] uppercase tracking-[0.3em] text-amber-700 mb-2">Daily Reminder</p>
            <p className="font-serif text-lg italic text-slate-800">
              “I don’t need to distrust everyone; I need to verify what matters before I put my money at risk.”
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default BusinessProtectionModal;
